// The local truth the learn screens paint from: the mirror of the learner's
// progress items, the queue of writes the edge has not confirmed yet, and the
// items the edge refused (kept on screen, retried next session). Writes land
// here first and are persisted before any request is made; the edge only ever
// improves what is shown. Persistence uses the asset cache directly, as the
// user tier does, but never as a swept data-cache entry: a learner's unsent
// work must not expire after 90 days offline.
import { useSyncExternalStore } from "react";
import { readAsset, writeAsset, removeAssetsWithPrefix } from "../assetCache";
import { POLICY } from "../dataCache";
import { ProgressItem, type LearnProgress } from "./contract";
import { isRecord } from "./guards";
import { compareUpdatedAt, mergeProgress } from "./merge";

const PREFIX = "user:learn:";
const MIRROR_KEY = `${PREFIX}progress`;
const QUEUE_KEY = `${PREFIX}queue`;
const VERSION = POLICY.user.version;

export interface LearnState {
  /** False until the saved copy has been read (one frame typical); screens show a skeleton until then. */
  readonly hydrated: boolean;
  /** The mirror: the edge's last merged answer with unconfirmed local writes on top. */
  readonly items: ReadonlyMap<string, ProgressItem>;
  /** Writes the edge has not confirmed, newest per key, with their original update times. */
  readonly queue: ReadonlyMap<string, ProgressItem>;
  /** Items the edge refused; still in the mirror, retried at the next session. */
  readonly aside: ReadonlyMap<string, ProgressItem>;
  readonly asideCount: number;
  /** When the edge last merged for us; undefined means this device never heard back. */
  readonly servedAt: string | undefined;
  /** The curriculum the mirror was last refreshed under; a change triggers a refresh, never a wipe. */
  readonly curriculumVersion: string | undefined;
}

const EMPTY: LearnState = {
  hydrated: false,
  items: new Map(),
  queue: new Map(),
  aside: new Map(),
  asideCount: 0,
  servedAt: undefined,
  curriculumVersion: undefined,
};

let state: LearnState = EMPTY;
let generation = 0;
let hydrating: Promise<void> | undefined;
let persistChain: Promise<void> = Promise.resolve();
const listeners = new Set<() => void>();
const writeListeners = new Set<() => void>();

function emit(next: LearnState): void {
  state = next;
  listeners.forEach((listener) => {
    listener();
  });
}

export function getLearnState(): LearnState {
  return state;
}

export function subscribeLearn(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useLearnState(): LearnState {
  return useSyncExternalStore(subscribeLearn, getLearnState, getLearnState);
}

/** Called after every local write, so sync can schedule a flush without the store knowing about the network. */
export function onLearnWrite(listener: () => void): () => void {
  writeListeners.add(listener);
  return () => writeListeners.delete(listener);
}

/** Bumped by every reset, so a request that was in flight at delete time cannot refill the mirror. */
export function getLearnGeneration(): number {
  return generation;
}

export function queuedItems(): ProgressItem[] {
  return [...state.queue.values()];
}

async function writeSnapshots(mirror: object, queue: object): Promise<void> {
  await writeAsset(MIRROR_KEY, VERSION, mirror);
  await writeAsset(QUEUE_KEY, VERSION, queue);
}

function persist(): void {
  const mirror = {
    items: [...state.items.values()],
    servedAt: state.servedAt,
    curriculumVersion: state.curriculumVersion,
  };
  const queue = { items: [...state.queue.values()], aside: [...state.aside.values()] };
  persistChain = persistChain.then(() => writeSnapshots(mirror, queue)).catch(() => undefined);
}

/** Resolves when everything written so far is on disk (tests, and the delete flow). */
export function settleLearn(): Promise<void> {
  return persistChain;
}

function readItems(value: unknown): ProgressItem[] {
  if (!Array.isArray(value)) return [];
  const items: ProgressItem[] = [];
  for (const candidate of value as unknown[]) {
    const parsed = ProgressItem.safeParse(candidate);
    if (parsed.success) items.push(parsed.data);
  }
  return items;
}

function field(value: unknown, name: string): unknown {
  return isRecord(value) ? value[name] : undefined;
}

const anything = (value: unknown): value is unknown => value !== undefined;

/** Reads the saved copy once; safe to call from every screen. */
export function hydrateLearn(): Promise<void> {
  hydrating ??= (async () => {
    const started = generation;
    const mirror = await readAsset(MIRROR_KEY, VERSION, anything);
    const queue = await readAsset(QUEUE_KEY, VERSION, anything);
    if (started !== generation) return;
    const queued = readItems(field(queue, "items"));
    const aside = readItems(field(queue, "aside"));
    // Sessions retry what the edge refused last time (its manifest may have caught up).
    const pending = mergeProgress(queued, aside);
    const servedAt = field(mirror, "servedAt");
    const curriculumVersion = field(mirror, "curriculumVersion");
    const items = mergeProgress(
      mergeProgress(readItems(field(mirror, "items")), pending.values()).values(),
      state.items.values(),
    );
    emit({
      hydrated: true,
      items,
      queue: mergeProgress(pending.values(), state.queue.values()),
      aside: new Map(),
      asideCount: 0,
      servedAt: typeof servedAt === "string" ? servedAt : undefined,
      curriculumVersion: typeof curriculumVersion === "string" ? curriculumVersion : undefined,
    });
  })();
  return hydrating;
}

/** A local write: mirror and queue first, then disk; never the network. */
export function recordItem(item: ProgressItem): void {
  if (!ProgressItem.safeParse(item).success) {
    console.error("learn: not storing a malformed item", item.key);
    return;
  }
  const have = state.items.get(item.key);
  if (have !== undefined && compareUpdatedAt(have.updatedAt, item.updatedAt) > 0) return;
  const items = new Map(state.items).set(item.key, item);
  const queue = new Map(state.queue).set(item.key, item);
  const aside = new Map(state.aside);
  aside.delete(item.key);
  emit({ ...state, items, queue, aside, asideCount: aside.size });
  persist();
  writeListeners.forEach((listener) => {
    listener();
  });
}

/**
 * The edge's merged progress replaces the mirror; what is still unconfirmed
 * (queued or set aside) stays on top where it is newer. A response from before
 * a delete is ignored.
 */
export function adoptServerProgress(
  progress: LearnProgress,
  forGeneration?: number,
  curriculumVersion?: string,
): void {
  if (forGeneration !== undefined && forGeneration !== generation) return;
  const items = mergeProgress(progress.items, [...state.queue.values(), ...state.aside.values()]);
  emit({
    ...state,
    items,
    servedAt: progress.servedAt,
    curriculumVersion: curriculumVersion ?? state.curriculumVersion,
  });
  persist();
}

/** Drops confirmed items from the queue, unless they were written again while the request was out. */
export function dequeueSent(sent: readonly ProgressItem[], forGeneration?: number): void {
  if (forGeneration !== undefined && forGeneration !== generation) return;
  const queue = new Map(state.queue);
  for (const item of sent) if (queue.get(item.key) === item) queue.delete(item.key);
  if (queue.size === state.queue.size) return;
  emit({ ...state, queue });
  persist();
}

/** The edge refused these keys: out of the queue so the rest syncs, still on screen, retried next session. */
export function setAside(keys: readonly string[], forGeneration?: number): void {
  if (forGeneration !== undefined && forGeneration !== generation) return;
  const queue = new Map(state.queue);
  const aside = new Map(state.aside);
  for (const key of keys) {
    const item = queue.get(key);
    if (item === undefined) continue;
    queue.delete(key);
    aside.set(key, item);
  }
  emit({ ...state, queue, aside, asideCount: aside.size });
  persist();
}

/** Forgets the in-memory state, as a restart would; what is on disk stays. */
export function resetLearnStore(): void {
  generation += 1;
  hydrating = undefined;
  emit(EMPTY);
}

/** Delete my data: the mirror, the queue and the saved copy go, on screen and on disk. */
export function clearLearnData(): Promise<void> {
  resetLearnStore();
  persistChain = persistChain.then(() => removeAssetsWithPrefix(PREFIX)).catch(() => undefined);
  return persistChain;
}
